import { useEffect, useState } from "react";
import {
  BodyComponent,
  HeaderComponent,
  SaveButton,
  SelectResourceList,
  SelectWithSearch,
  trans,
} from "@comps/components";
import { Alert, Card, Col, Form, Row, Skeleton, Space, Typography, message } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useNavigate } from "react-router-dom";
import { createSelectList } from "@comps/commonFunctions";
import { makeRequest } from "@utils/helpers";
import { Const } from "../../constants";
import { createDataReq, getDashboardDirectivesReq, getDashboardTypes } from "../requests";
import CustomDashboardBuilder from "./CustomDashboardBuilder";
import {
  applyLayoutToRow,
  clearWidgetFromRows,
  createEmptyRow,
  getColumnLength,
  moveRowPosition,
} from "./customDashboardBuilderUtils";

const { Paragraph, Text, Title } = Typography;

const langKey = Const.customDashboard.lngKey;
const formName = "createCustomDashboard";

const pageConfig = {
  headers: {
    title: trans(`${langKey}.moduleName`),
    breadcrumb: [
      {
        name: trans(`${langKey}.moduleName`),
        path: Const.customDashboard.route,
      },
    ],
  },
};

const sectionCardStyle = {
  borderRadius: 16,
  border: "1px solid #d7e3f4",
  boxShadow: "0 10px 28px rgba(20, 54, 96, 0.08)",
};

const CreateCustomDashboard = (props) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loader, setLoader] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboardTypes, setDashboardTypes] = useState([]);
  const [roles, setRoles] = useState([]);
  const [forEmployment, setForEmployment] = useState(false);
  const [widgets, setWidgets] = useState([]);
  const [rows, setRows] = useState([createEmptyRow()]);

  useEffect(() => {
    getRelatedData();
    // eslint-disable-next-line
  }, []);

  const getRelatedData = () => {
    makeRequest(setLoader, getDashboardDirectivesReq, null, onGetDirectivesSuccess, onGetDirectiveError);
    makeRequest(setLoader, getDashboardTypes, null, onGetDashboardTypes, onGetDashboardTypesError);
  };

  const onGetDirectivesSuccess = (response) => {
    setRoles(createSelectList(response.roles));
    setWidgets(response.dashboard_directives || []);
    setLoading(false);
  };

  const onGetDashboardTypes = (response) => {
    const result = Object.values(response.resources.data).map((item) => ({
      label: item.name,
      value: item.id,
    }));
    setDashboardTypes(result);
    setLoading(false);
  };

  const onGetDashboardTypesError = () => {
    setLoading(false);
  };

  const onGetDirectiveError = (err, res) => {
    message.error(res?.response?.data?.message || "Unable to load widget directives.");
    setLoading(false);
  };

  const formatRowsForPayload = (builderRows) => ({
    rows: builderRows.map((row, rowIndex) => ({
      order: rowIndex,
      columns: row.columns.map((column) => ({
        length: getColumnLength(column.width),
        dashboard_directive_id: column.widgets.length > 0 ? column.widgets[0] : null,
      })),
    })),
  });

  const onSubmit = (data) => {
    const dashboardRows = formatRowsForPayload(rows);
    const payload = {
      custom_dashboard: {
        employment_id: forEmployment ? data.employment_id : null,
        role_id: !forEmployment ? data.role_id : null,
        for_employment: forEmployment,
        type: data.type,
        rows: dashboardRows.rows,
        dashboard_rows: dashboardRows.rows,
      },
    };

    makeRequest(setLoader, createDataReq, payload, onSuccess, onError);
    setSaving(true);
  };

  const onSuccess = () => {
    message.success("Saved successfully");
    goBack();
  };

  const onError = (err, res) => {
    setSaving(false);
    message.error(res?.response?.data?.message || "Unable to save custom dashboard.");
  };

  const goBack = () => {
    navigate("/workflowManagement/customDashboard/index");
  };

  const handleMoveRow = (index, direction) => {
    setRows((prevRows) => moveRowPosition(prevRows, index, direction));
  };

  const handleRemoveRow = (indexToRemove) => {
    setRows((prevRows) => prevRows.filter((_, index) => index !== indexToRemove));
  };

  const handleAddRow = () => {
    setRows((prevRows) => [...prevRows, createEmptyRow()]);
  };

  const handleClearWidget = (rowIndex, colIndex) => {
    setRows((prevRows) => clearWidgetFromRows(prevRows, rowIndex, colIndex));
  };

  const handleSplitColumn = (rowIndex, colIndex, widths) => {
    setRows((prevRows) => {
      const nextRows = applyLayoutToRow(prevRows, rowIndex, colIndex, widths);
      if (nextRows[rowIndex]?.columns.length === prevRows[rowIndex]?.columns.length && widths.length > 1) {
        message.warning("Cannot split the column. The row can have a maximum of 4 columns.");
      }
      return nextRows;
    });
  };

  return (
    <BodyComponent>
      <HeaderComponent headers={ pageConfig.headers } modkey={ props.modkey }>

        <SaveButton text="Back" icon={ <ArrowLeftOutlined /> } onClick={ goBack } />
      </HeaderComponent>

      <Skeleton loading={ loading }>
        <Space direction="vertical" size={ 24 } style={ { width: "100%" } }>
          <Card style={ sectionCardStyle } bodyStyle={ { padding: 20 } }>
            <Space direction="vertical" size={ 18 } style={ { width: "100%" } }>
              <div>
                <Title level={ 4 } style={ { marginBottom: 4, color: "#20324b" } }>
                  Dashboard Setup
                </Title>
                <Paragraph type="secondary" style={ { marginBottom: 0 } }>
                  Choose who this dashboard is for and which dashboard type it should configure before arranging widgets below.
                </Paragraph>
              </div>

              {/* <Alert
                type="info"
                showIcon
                message="Builder workflow"
                description="Select a slot in the layout first, then drag a widget into it or use the Add button in the widget library for quick placement."
              /> */}

              <Form form={ form } name={ formName } layout="vertical" onFinish={ onSubmit } initialValues={ { for_employment: false } }>
                <Row gutter={ [16, 0] }>
                  <Col lg={ 8 } md={ 12 } xs={ 24 }>
                    <Form.Item name="for_employment" label="Audience" rules={ rules.for_employment }>
                      <SelectWithSearch
                        options={ [
                          {
                            label: "Role",
                            value: false,
                          },
                          {
                            label: "Employee",
                            value: true,
                          },
                        ] }
                        onChange={ (value) => setForEmployment(value) }
                        placeholder="Select audience"
                      />
                    </Form.Item>
                  </Col>

                  { forEmployment ? (
                    <Col lg={ 8 } md={ 12 } xs={ 24 }>
                      <Form.Item name="employment_id" label="Employee" rules={ rules.employment_id }>
                        <SelectResourceList type="employment" placeholder="Search employee" />
                      </Form.Item>
                    </Col>
                  ) : (
                    <Col lg={ 8 } md={ 12 } xs={ 24 }>
                      <Form.Item name="role_id" label="Role" rules={ rules.role_id }>
                        <SelectWithSearch options={ roles } placeholder="Select role" />
                      </Form.Item>
                    </Col>
                  ) }

                  <Col lg={ 8 } md={ 12 } xs={ 24 }>
                    <Form.Item name="type" label="Dashboard Type" rules={ rules.dashboard_type }>
                      <SelectWithSearch options={ dashboardTypes } placeholder={ trans(`${langKey}.type`) } />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Space>
          </Card>

          <SaveButton text="Publish" key="create_button" htmlType="submit" form={ formName } loading={ saving || loader } />
          <DndProvider backend={ HTML5Backend }>
            <CustomDashboardBuilder
              widgets={ widgets }
              rows={ rows }
              onChangeRows={ setRows }
              onAddRow={ handleAddRow }
              onMoveRow={ handleMoveRow }
              onRemoveRow={ handleRemoveRow }
              onClearWidget={ handleClearWidget }
              onSplitColumn={ handleSplitColumn }
            />
          </DndProvider>

          <Text type="secondary">
            Each slot can hold one widget. Moving or reassigning a widget removes it from its previous slot automatically.
          </Text>
        </Space>
      </Skeleton>
    </BodyComponent>
  );
};

export default CreateCustomDashboard;

const rules = {
  role_id: [
    { required: true, message: "Role is required" },
  ],
  employment_id: [
    { required: true, message: "Employee is required" },
  ],
  dashboard_type: [
    { required: true, message: trans(`${langKey}.dashboard_type`) },
  ],
  for_employment: [
    { required: true, message: "Select whether this dashboard is for a role or an employee." },
  ],
};
